import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Step,
  stepLabelClasses,
  tabClasses,
  Typography,
  useTheme
} from '@mui/material';

import { PostTalentsResponse, ProcessResponseDTO } from '@/api/generated';
import LoadingButton from '@/components/LoadingButton';
import { VerticalStepLabel, VerticalStepper } from '@/components/VerticalStepper';
import useTalentBasicInfoForm from '@/hooks/useTalentBasicInfoForm';
import { ROUTER_PATHS, toTalent } from '@/root/linker';
import AdditionalInfo from '@/views/TalentEditInfoModal/AdditionalInfo';
import {
  getAdditionalInfoKey,
  TalentBasicInfoViewType,
  TalentModalEditSection
} from '@/views/TalentEditInfoModal/constants';
import TalentAddToProcessView from '@/views/TalentsCreate/TalentAddToProcessView';
import TalentBasicInfoView from '@/views/TalentsCreate/TalentBasicInfoView';
import TalentInfoRow from '@/views/TalentsCreate/TalentInfoRow';

const TalentsCreate = () => {
  const { spacing, palette } = useTheme();
  const { t } = useTranslation(['talents', 'filters', 'settings']);
  const location = useLocation();
  const navigate = useNavigate();
  const { formMethods } = useTalentBasicInfoForm<{ selectedProcesses?: Map<string, ProcessResponseDTO> }>({
    viewType: TalentBasicInfoViewType.CREATE
  });
  const { watch } = formMethods;
  const isLoaded = useRef(false);
  const [activeStep, setActiveStep] = useState<string>(TalentModalEditSection.ADD_TO_PROCESS);
  const organizationSlug = watch('organizationSlug');
  const selectedProcesses = watch('selectedProcesses');
  const selectedProcessesArr = useMemo(
    () => (selectedProcesses ? Array.from(selectedProcesses.values()) : []),
    [selectedProcesses]
  );
  const [completedProcesses, setCompletedProcesses] = useState<string[]>([]);
  const [createdTalent, setCreatedTalent] = useState<PostTalentsResponse | undefined>();

  const handleSubmitBasicInfo = useCallback(() => setActiveStep(TalentModalEditSection.ADD_TO_PROCESS), []);
  const handleGoBackAddToProcess = useCallback(() => setActiveStep(TalentModalEditSection.BASIC_INFO), []);
  const handleGoNextAddToProcess = useCallback(
    (createdTalent: PostTalentsResponse) => {
      setActiveStep(getAdditionalInfoKey(selectedProcessesArr[0]));
      setCreatedTalent(createdTalent);
    },
    [selectedProcessesArr]
  );

  const locationSlugsInState = location.state?.locations?.length
    ? (location.state as { locations: string[] }).locations
    : null;

  useEffect(() => {
    if (!locationSlugsInState && !createdTalent) {
      navigate(ROUTER_PATHS.TALENTS, { replace: true });

      return;
    }

    if (isLoaded.current) return;

    if (location.state) {
      formMethods.reset(location.state);
      isLoaded.current = true;
    }
  }, [formMethods, location.state, locationSlugsInState, createdTalent, navigate]);

  if (!locationSlugsInState && !createdTalent) {
    return null;
  }

  return (
    <FormProvider {...formMethods}>
      <Stack direction="column" sx={{ height: '100%', textAlign: 'start' }}>
        <TalentInfoRow talent={createdTalent} />
        <Stack direction="row" flexGrow={1} sx={{ padding: spacing(3, 5) }}>
          <VerticalStepper sx={{ alignSelf: 'flex-start', padding: spacing(3, 2), width: spacing(30) }}>
            <Step
              active={activeStep === TalentModalEditSection.BASIC_INFO}
              completed={activeStep !== TalentModalEditSection.BASIC_INFO}
            >
              <VerticalStepLabel
                sx={{
                  [`& .${stepLabelClasses.label}`]: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start'
                  }
                }}
              >
                {t('layouts.talentEditModal.tabs.basicInfo')}
              </VerticalStepLabel>
            </Step>

            <Step active={activeStep === TalentModalEditSection.ADD_TO_PROCESS} completed={!!createdTalent}>
              <VerticalStepLabel
                sx={{
                  [`& .${stepLabelClasses.label}`]: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start'
                  }
                }}
              >
                {t('layouts.talentEditModal.tabs.addToProcess')}
                <Typography
                  component="span"
                  variant="body4"
                  sx={{
                    marginTop: spacing(0.5),
                    [`${tabClasses.root}:not(${tabClasses.selected}) &`]: { color: palette.grey[600] }
                  }}
                >
                  {t('common:optional')}
                </Typography>
              </VerticalStepLabel>
            </Step>

            {selectedProcessesArr.map(process => (
              <Step
                key={process.id}
                active={activeStep === getAdditionalInfoKey(process)}
                completed={completedProcesses.includes(process.id!)}
              >
                <VerticalStepLabel
                  sx={{
                    [`& .${stepLabelClasses.label}`]: {
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start'
                    }
                  }}
                >
                  <>
                    {t('layouts.talentEditModal.tabs.additionalInfo')}
                    <Chip variant="tag" label={process.name} sx={{ marginTop: spacing(0.5) }} />
                    <Typography
                      component="span"
                      variant="body4"
                      sx={{
                        marginTop: spacing(0.5),
                        [`${tabClasses.root}:not(${tabClasses.selected}) &`]: { color: palette.grey[600] }
                      }}
                    >
                      {t('common:optional')}
                    </Typography>
                  </>
                </VerticalStepLabel>
              </Step>
            ))}
          </VerticalStepper>

          <Paper
            elevation={0}
            sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, backgroundColor: 'background.default' }}
          >
            {activeStep === TalentModalEditSection.BASIC_INFO && (
              <TalentBasicInfoView onSubmit={handleSubmitBasicInfo} />
            )}

            {activeStep === TalentModalEditSection.ADD_TO_PROCESS && !!organizationSlug && (
              <TalentAddToProcessView onGoBack={handleGoBackAddToProcess} onGoNext={handleGoNextAddToProcess} />
            )}

            {selectedProcessesArr.map((process, i, processes) => {
              const isLast = i === processes.length - 1;
              const isFirst = i === 0;
              if (createdTalent && activeStep === getAdditionalInfoKey(process)) {
                return (
                  <AdditionalInfo
                    key={process.id}
                    process={process}
                    talentId={createdTalent.id!}
                    footer={
                      <TalentAdditionalInfoFooter
                        talent={createdTalent}
                        isFirst={isFirst}
                        isLast={isLast}
                        onGoBack={() => setActiveStep(getAdditionalInfoKey(processes[i - 1]))}
                        onGoNext={() => {
                          setActiveStep(getAdditionalInfoKey(processes[i + 1]));
                          setCompletedProcesses([...completedProcesses, process.id!]);
                        }}
                      />
                    }
                  />
                );
              }
            })}
          </Paper>
        </Stack>
      </Stack>
    </FormProvider>
  );
};

export default TalentsCreate;

const TalentAdditionalInfoFooter = ({
  isLast,
  isFirst,
  onGoBack,
  onGoNext,
  talent
}: {
  isLast: boolean;
  isFirst: boolean;
  onGoBack(): void;
  onGoNext(): void;
  talent: PostTalentsResponse;
}) => {
  const { formState } = useFormContext();
  const { palette, spacing } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleFinish = useCallback(() => {
    navigate(toTalent({ talentId: talent.id! }));
  }, [navigate, talent.id]);

  return (
    <>
      <Divider sx={{ marginTop: 'auto', borderColor: palette.grey[400] }} />
      <Stack direction="row" sx={{ padding: spacing(2.5, 2, 2), gap: spacing(1.5) }}>
        {!isFirst && <Button onClick={onGoBack}>{t('common:actions.back')}</Button>}
        <Box sx={{ marginInlineStart: 'auto', marginInlineEnd: spacing(2) }}>
          {!isLast && (
            <Button color="primary" onClick={onGoNext} sx={{ marginInlineEnd: spacing(2) }}>
              {t('common:actions.skip')}
            </Button>
          )}
          <LoadingButton
            type="submit"
            color="primary"
            variant="contained"
            disabled={!formState.isDirty}
            isLoading={formState.isSubmitting}
          >
            {t('common:actions.save')}
          </LoadingButton>
          {isLast && (
            <Button color="primary" variant="contained" onClick={handleFinish} sx={{ marginInlineStart: spacing(2) }}>
              {t('common:actions.finish')}
            </Button>
          )}
        </Box>
      </Stack>
    </>
  );
};
